import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { PublicKey } from "@solana/web3.js";
import { toast } from "sonner";
import {
  Pencil,
  Trash2,
  Plus,
  Check,
  X,
  Copy,
  Search,
  Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface SavedWallet {
  address: string;
  name: string;
  timestamp: number;
  lastUsed?: number;
}

interface WalletManagerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWallet: (address: string) => void;
}

export function WalletManagerPanel({
  isOpen,
  onClose,
  onSelectWallet,
}: WalletManagerPanelProps) {
  const [savedWallets, setSavedWallets] = useState<SavedWallet[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const wallets = localStorage.getItem("saved_wallets");
    if (wallets) {
      setSavedWallets(JSON.parse(wallets));
    }
  }, []);

  const saveWallets = (wallets: SavedWallet[]) => {
    localStorage.setItem("saved_wallets", JSON.stringify(wallets));
    setSavedWallets(wallets);
  };

  const handleQuickAdd = () => {
    if (!newAddress) return;

    try {
      new PublicKey(newAddress);

      const wallet: SavedWallet = {
        address: newAddress,
        name: `Wallet ${savedWallets.length + 1}`,
        timestamp: Date.now(),
        lastUsed: Date.now(),
      };

      const updatedWallets = [...savedWallets, wallet].sort(
        (a, b) => (b.lastUsed || b.timestamp) - (a.lastUsed || a.timestamp)
      );

      saveWallets(updatedWallets);
      setNewAddress("");
      toast.success("Wallet saved!");
    } catch (error) {
      toast.error("Invalid Solana address");
    }
  };

  const handleEditName = (address: string, newName: string) => {
    const updatedWallets = savedWallets.map((wallet) =>
      wallet.address === address ? { ...wallet, name: newName } : wallet
    );
    saveWallets(updatedWallets);
    setEditingId(null);
    toast.success("Wallet name updated!");
  };

  const handleDeleteWallet = (address: string) => {
    const updatedWallets = savedWallets.filter((w) => w.address !== address);
    saveWallets(updatedWallets);
    toast.success("Wallet removed!");
  };

  const handleSelect = (address: string) => {
    const updatedWallets = savedWallets
      .map((w) => (w.address === address ? { ...w, lastUsed: Date.now() } : w))
      .sort(
        (a, b) => (b.lastUsed || b.timestamp) - (a.lastUsed || a.timestamp)
      );

    saveWallets(updatedWallets);
    onSelectWallet(address);
  };

  const filteredWallets = savedWallets.filter(
    (wallet) =>
      wallet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed right-4 top-20 w-[380px] max-h-[600px] bg-[#111111]/90 backdrop-blur-xl border border-[#ffffff]/10 rounded-2xl shadow-2xl z-50 overflow-hidden"
          >
            <div className="flex flex-col h-full">
              {/* Quick Add */}
              <div className="p-4 border-b border-[#ffffff]/10">
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste Solana address"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    className="bg-[#ffffff]/5 border-[#ffffff]/10"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleQuickAdd();
                    }}
                  />
                  <Button
                    onClick={handleQuickAdd}
                    className="bg-gradient-to-r from-[#9945FF] to-[#00D18C] hover:opacity-90 px-6"
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Search & Header */}
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-[#9945FF]" />
                    <h2 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#9945FF] to-[#00D18C]">
                      Saved Wallets
                    </h2>
                  </div>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#ffffff]/30" />
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-8 w-[120px] bg-[#ffffff]/5 border-[#ffffff]/10 focus:w-[180px] transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Wallet List */}
              <div className="flex-1 overflow-y-auto max-h-[400px]">
                <div className="px-4 space-y-2">
                  {filteredWallets.map((wallet) => (
                    <motion.div
                      key={wallet.address}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={cn(
                        "group relative p-3 rounded-xl transition-all",
                        "bg-gradient-to-r from-[#ffffff]/[0.03] to-[#ffffff]/[0.05]",
                        "hover:from-[#ffffff]/[0.05] hover:to-[#ffffff]/[0.07]",
                        "border border-[#ffffff]/[0.05] hover:border-[#ffffff]/10",
                        editingId === wallet.address && "ring-1 ring-[#9945FF]"
                      )}
                    >
                      <div className="space-y-1.5">
                        {editingId === wallet.address ? (
                          <div className="flex items-center gap-2">
                            <Input
                              defaultValue={wallet.name}
                              onChange={(e) => setNewName(e.target.value)}
                              className="h-7 bg-[#ffffff]/10 border-[#ffffff]/10"
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() =>
                                handleEditName(wallet.address, newName)
                              }
                            >
                              <Check className="h-3 w-3 text-green-500" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => setEditingId(null)}
                            >
                              <X className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-white">
                                {wallet.name}
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 hover:text-[#9945FF]"
                                  onClick={() => setEditingId(wallet.address)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 hover:text-[#9945FF]"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      wallet.address
                                    );
                                    toast.success("Address copied!");
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 hover:text-red-500"
                                  onClick={() =>
                                    handleDeleteWallet(wallet.address)
                                  }
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm text-[#ffffff]/50 truncate">
                                {wallet.address}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 shrink-0 hover:bg-[#ffffff]/10 hover:text-[#9945FF] text-xs"
                                onClick={() => handleSelect(wallet.address)}
                              >
                                Select
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 text-xs text-center text-[#ffffff]/30">
                {savedWallets.length} saved wallet
                {savedWallets.length !== 1 ? "s" : ""}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
